import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Panel, Button } from 'react-bootstrap';
import ApplyAction from '../../atoms/ApplyAction/ApplyAction';
import componentsWithProps from '../../utils/componentsWithProps';
import GridLayout from '../../molecules/GridLayout/GridLayout';
import InlineLayout from '../../molecules/InlineLayout/InlineLayout';
import { withTranslation } from '../../translations/i18n';
import withIdContext from '../../decorators/withIdContext';

const GRID = 'grid';
const INLINE = 'inline';

const layoutsMap = {
  [GRID]: GridLayout,
  [INLINE]: InlineLayout,
};

class Filters extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    /** Number of columns */
    cols: PropTypes.number,
    /** Whether it renders filters in grid or inline */
    type: PropTypes.oneOf([GRID, INLINE]),
    /** Initial values. Initial values have precedence over values on filter components */
    initialValues: PropTypes.object,
    /** Array of filters (can be single filter) */
    children: componentsWithProps('name', 'value', 'onChange').isRequired,
    /** Gets called with filters data after click on Filters button */
    onSubmit: PropTypes.func,
    /** Shows clear filters button when given, expected to return filter values to reset to when called. Not shown inline. */
    onClear: PropTypes.func,
  }

  static defaultProps = {
    cols: 1,
    type: GRID,
    initialValues: {},
    onSubmit: () => {},
    onClear: null,
  }

  constructor(props) {
    super(props);

    const values = this.getInitialValues();
    this.state = {
      values,
    };
  }

  onChange(childOnChange, name, value) {
    this.setState({
      values: {
        ...this.state.values,
        [name]: value,
      },
    });
    if (childOnChange) {
      childOnChange(value);
    }
  }

  onSubmit = ({ values }) => {
    const { onSubmit } = this.props;
    onSubmit(values || this.state.values);
  }

  onClearClicked = () => {
    const { onClear } = this.props;
    const resetValues = this.getValues(onClear());
    this.setState({ values: resetValues });
    this.onSubmit({ values: resetValues });
  }

  getChildrenArray() {
    const { children } = this.props;
    return React.Children.toArray(children);
  }

  getInitialValues() {
    const { initialValues } = this.props;
    return this.getValues(initialValues);
  }

  getValues(values) {
    const childrenArr = this.getChildrenArray();

    return childrenArr.reduce((result, child) => {
      const { name, value } = child.props;
      result[name] = (values && values[name]) || value; // eslint-disable-line no-param-reassign
      return result;
    }, {});
  }

  renderChildren() {
    const childrenArr = this.getChildrenArray();

    return childrenArr.map((child) => {
      const { name, onChange } = child.props;
      return React.cloneElement(child, {
        value: this.state.values[name],
        onChange: this.onChange.bind(this, onChange, name),
      });
    });
  }

  renderApplyButton() {
    const { t } = this.props;
    return (
      <ApplyAction id="ApplyFilters" key="ApplyFilters" label={t('Apply Filters')} onClick={() => this.onSubmit({})} />
    );
  }

  renderClearButton() {
    const { t, onClear } = this.props;
    return (
      onClear && (
        <Button
          bStyle="secondary"
          type="reset"
          onClick={this.onClearClicked}
          id="ClearFilters"
          key="ClearFilters"
          className="mr-2"
        >
          {t('Clear Filters')}
        </Button>
      )
    );
  }

  render() {
    const { cols, type } = this.props;

    const Layout = layoutsMap[type];

    return (
      <div className="Filters">
        <Panel>
          <Panel.Body>
            <div className={type}>
              <Layout cols={cols}>
                { this.renderChildren() }
              </Layout>
              { type === INLINE && this.renderApplyButton() }
            </div>
          </Panel.Body>
          { type !== INLINE &&
            <Panel.Footer align="right">
              { this.renderClearButton() }
              { this.renderApplyButton() }
            </Panel.Footer>
          }
        </Panel>
      </div>
    );
  }
}

export const FiltersBase = Filters;

const FiltersTranslated = withTranslation()(Filters);
export default withIdContext('Filters')(FiltersTranslated);
